import React, {
    useContext, useMemo
} from 'react';
import {
    useLocation, useNavigate, useParams
} from 'react-router-dom';
import {
    Layout, Sparkles
} from 'lucide-react';
import {
    Context
} from 'shared/context/GlobalState';
import {
    cn
} from 'shared/lib/utils';
import {
    APP_STUDIO_ENABLED
} from 'shared/config/featureFlags';
import type {
    SubOrganizationMinimal
} from 'shared/context/context.types';
import './StudioProductTabs.css';

function resolveSubOrgId(
    subOragID: string | undefined,
    subOrganizations: SubOrganizationMinimal[] | undefined,
): string | null {
    if (subOragID) return subOragID;
    const stored = localStorage.getItem('selectedSubOrg');
    if (stored && subOrganizations?.some((s) => s._id === stored)) return stored;
    const firstId = subOrganizations?.find((s) => s._id)?._id;
    return firstId ?? null;
}

export default function StudioProductTabs({
    className,
}: {
    className?: string;
}) {
    const navigate = useNavigate();
    const {
        pathname
    } = useLocation();
    const {
        oragID, subOragID
    } = useParams();
    const {
        organizations
    } = useContext(Context);

    const currentOrg = useMemo(
        () => organizations?.find((o) => o._id === oragID),
        [organizations, oragID],
    );

    const onDeStudio = pathname.includes('/de-studio');
    const onAppStudio = pathname.includes('/3dapp-studio');

    if (!oragID || (!onDeStudio && !onAppStudio)) return null;

    const goDeStudio = () => {
        const subId = resolveSubOrgId(subOragID, currentOrg?.subOrganizations);
        if (subId) {
            navigate(`/organizations/${oragID}/sub/${subId}/de-studio`);
        } else {
            navigate(`/organizations/${oragID}/sub`);
        }
    };

    const goAppStudio = () => {
        navigate(`/organizations/${oragID}/3dapp-studio`);
    };

    return (
        <div className={cn('bex-studio-tabs', className)} role="tablist" aria-label="Studio">
            <StudioTab
                label="DE Studio"
                icon={Sparkles}
                active={onDeStudio}
                onClick={goDeStudio}
            />
            {APP_STUDIO_ENABLED && (
                <StudioTab
                    label="3D App Studio"
                    icon={Layout}
                    active={onAppStudio}
                    onClick={goAppStudio}
                />
            )}
        </div>
    );
}

function StudioTab({
    label,
    icon: Icon,
    active,
    onClick,
}: {
    label: string;
    icon: React.ComponentType<{ size?: number }>;
    active: boolean;
    onClick: () => void;
}) {
    return (
        <button
            type="button"
            role="tab"
            aria-selected={active}
            onClick={onClick}
            className={cn('bex-studio-tab', active && 'bex-studio-tab-active')}
        >
            <Icon size={13} />
            {label}
        </button>
    );
}
